import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useAction, useMutation } from 'convex/react'
import { api } from '../../../../../convex/_generated/api'
import { Id } from '../../../../../convex/_generated/dataModel'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  ArrowLeft,
  Loader2,
  User,
  Film,
  FolderOpen,
  Sparkles,
  Clock,
  ExternalLink,
  Play,
  AlertCircle,
  Database,
  Ban,
  CheckCircle,
} from 'lucide-react'

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    succeeded: 'bg-green-100 text-green-700',
    completed: 'bg-green-100 text-green-700',
    processing: 'bg-blue-100 text-blue-700',
    generating: 'bg-blue-100 text-blue-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-neutral-100 text-neutral-500',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] ?? 'bg-neutral-100 text-neutral-600'}`}>
      {status}
    </span>
  )
}

const SECTION_LIMIT = 20

function VideoGrid({ videos, total, emptyMessage }: { videos: any[]; total: number; emptyMessage: string }) {
  if (videos.length === 0) {
    return <p className="text-sm text-neutral-400 text-center py-6">{emptyMessage}</p>
  }
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {videos.map((video: any) => (
        <div
          key={video._id || video.id}
          className="group rounded-lg border border-neutral-100 overflow-hidden hover:border-neutral-200 transition-colors"
        >
          <div className="aspect-video bg-neutral-100 relative">
            {video.thumbnailUrl ? (
              <img
                src={video.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Play className="h-6 w-6 text-neutral-300" />
              </div>
            )}
            <div className="absolute top-1 right-1">
              <StatusBadge status={video.status} />
            </div>
          </div>
          <div className="p-2">
            <p className="text-xs text-neutral-700 line-clamp-2 leading-relaxed">
              {video.prompt || video.title || 'No prompt'}
            </p>
            <p className="text-[10px] text-neutral-400 mt-1">
              {formatDate(video.createdAt)}
            </p>
          </div>
        </div>
      ))}
      </div>
      {total > videos.length && (
        <p className="text-xs text-neutral-400 text-center mt-3">
          Showing {videos.length} of {total}
        </p>
      )}
    </div>
  )
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const userId = id as Id<"users">

  const data = useQuery(api.admin.getUserDetail, { userId })
  const getLegacyVideos = useAction(api.adminActions.getUserLegacyVideos)
  const suspendUser = useMutation(api.admin.suspendUser)

  const handleToggleSuspend = async (currentlySuspended: boolean) => {
    const action = currentlySuspended ? 'unsuspend' : 'suspend'
    if (confirm(`Are you sure you want to ${action} this user?`)) {
      await suspendUser({ userId, suspend: !currentlySuspended })
    }
  }

  const [legacyVideos, setLegacyVideos] = useState<any[] | null>(null)
  const [legacyLoading, setLegacyLoading] = useState(false)

  // Fetch legacy videos once profile data is available
  useEffect(() => {
    if (!data?.profile?.supabaseUserId) return
    setLegacyLoading(true)
    getLegacyVideos({ userId })
      .then((videos) => setLegacyVideos(videos as any[]))
      .catch(() => setLegacyVideos([]))
      .finally(() => setLegacyLoading(false))
  }, [data?.profile?.supabaseUserId, userId])

  if (data === undefined) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="p-6 space-y-6">
        <Link to="/admin/users" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">User not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { user, profile, videos, workflows } = data

  if (!user || !profile) {
    return (
      <div className="p-6 space-y-6">
        <Link to="/admin/users" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">User data unavailable</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Back link */}
      <Link to="/admin/users" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700">
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {user.image ? (
            <img src={user.image} alt="" className="w-14 h-14 rounded-full" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center">
              <User className="h-7 w-7 text-neutral-400" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-neutral-900">
                {user.name || profile.name || 'No name'}
              </h1>
              {profile.isSuspended && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
                  <Ban className="h-3 w-3" />
                  Suspended
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-500">{user.email || profile.email}</p>
            <p className="text-xs text-neutral-400 mt-0.5">
              Joined {formatDate(user._creationTime)}
            </p>
          </div>
        </div>
        <Button
          variant={profile.isSuspended ? 'outline' : 'destructive'}
          size="sm"
          onClick={() => handleToggleSuspend(!!profile.isSuspended)}
          className="flex items-center gap-2"
        >
          {profile.isSuspended ? (
            <><CheckCircle className="h-4 w-4" /> Unsuspend</>
          ) : (
            <><Ban className="h-4 w-4" /> Suspend User</>
          )}
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <Sparkles className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900">
                  {profile.creditsUsedThisPeriod.toLocaleString()}
                </p>
                <p className="text-xs text-neutral-500">Credits this period</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900">
                  {profile.creditsUsedLifetime.toLocaleString()}
                </p>
                <p className="text-xs text-neutral-500">Credits lifetime</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FolderOpen className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900">{workflows.length}</p>
                <p className="text-xs text-neutral-500">Videos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Film className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-neutral-900">{videos.length}</p>
                <p className="text-xs text-neutral-500">Videos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Videos ({workflows.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {workflows.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-6">No videos yet</p>
          ) : (
            <div className="space-y-2">
              {workflows.slice(0, SECTION_LIMIT).map((workflow: any) => (
                <div
                  key={workflow._id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-50 transition-colors border border-neutral-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {workflow.coverImageUrl ? (
                      <img
                        src={workflow.coverImageUrl}
                        alt=""
                        className="w-12 h-8 rounded object-cover bg-neutral-100 shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-8 rounded bg-neutral-100 flex items-center justify-center shrink-0">
                        <FolderOpen className="h-4 w-4 text-neutral-300" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {workflow.title || 'Untitled'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-neutral-400">
                        <StatusBadge status={workflow.status} />
                        <span>{workflow.clipCount} clips</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(workflow.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <a
                    href={`/video/${workflow.shortcode ?? workflow._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-400 hover:text-neutral-600 shrink-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              ))}
              {workflows.length > SECTION_LIMIT && (
                <p className="text-xs text-neutral-400 text-center mt-3">
                  Showing {SECTION_LIMIT} of {workflows.length}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Videos Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Videos ({videos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <VideoGrid videos={videos.slice(0, SECTION_LIMIT)} total={videos.length} emptyMessage="No videos yet" />
        </CardContent>
      </Card>

      {/* Supabase Videos Section */}
      {profile.supabaseUserId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-neutral-400" />
              Supabase Videos {legacyVideos !== null && `(${legacyVideos.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {legacyLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              </div>
            ) : (
              <VideoGrid videos={(legacyVideos ?? []).slice(0, SECTION_LIMIT)} total={(legacyVideos ?? []).length} emptyMessage="No legacy videos" />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
